import type { SupportedFieldProperty } from '../../../../../../../../../common/types';
import type { FieldPublicShape } from '../../../../../../../../../common/types/field';
import { FIELD_VALIDATION_ERROR } from '../../../../../../../../../controllers/issue-adjustments/utils/errors/constants';
import type { FieldValidationError } from '../../../../../../../../../controllers/issue-adjustments/utils/errors/types';
import { mapFieldPropertyToApiMethod } from '../../../../../../../../../controllers/issue-adjustments/utils/map-field-property-to-api-method';

import { VALID_RESULT } from './constants';
import type { InvalidPropertyValidationResult, PropertyValidationResult } from './types';

const prepareValidationResultByBooleanValue = ({
	validationResult,
	fieldId,
	fieldType,
	propertyName,
}: {
	validationResult: boolean;
	fieldId: string;
	fieldType: string;
	propertyName: SupportedFieldProperty;
}): PropertyValidationResult => {
	if (validationResult === false) {
		return {
			isValid: false,
			error: {
				type: FIELD_VALIDATION_ERROR,
				method: mapFieldPropertyToApiMethod(propertyName),
				fieldId,
				fieldType,
			},
		};
	}
	return VALID_RESULT;
};

type ExtendedValidationResultValue =
	| {
			isValid: false;
			error: Pick<FieldValidationError, 'method' | 'message'>;
	  }
	| { isValid: true };

const prepareValidationResultByExtendedValue = ({
	validationResult,
	fieldId,
	fieldType,
}: {
	validationResult: ExtendedValidationResultValue;
	fieldId: string;
	fieldType: string;
}): PropertyValidationResult => {
	if (validationResult.isValid === false) {
		return {
			isValid: false,
			error: {
				type: FIELD_VALIDATION_ERROR,
				method: validationResult.error.method,
				message: validationResult.error.message,
				fieldId,
				fieldType,
			},
		};
	}
	return VALID_RESULT;
};

export const prepareValidationResult = ({
	validationResult,
	fieldId,
	fieldType,
	propertyName,
}: {
	validationResult: boolean | ExtendedValidationResultValue;
	fieldId: string;
	fieldType: string;
	propertyName: SupportedFieldProperty;
}): PropertyValidationResult => {
	// We will remove the boolean check and old method after changing all validators to return objects
	if (typeof validationResult === 'boolean') {
		return prepareValidationResultByBooleanValue({
			validationResult,
			fieldId,
			fieldType,
			propertyName,
		});
	}

	return prepareValidationResultByExtendedValue({ validationResult, fieldId, fieldType });
};

export const filterInvalidResults = (validationResults: PropertyValidationResult[]) =>
	validationResults.reduce((acc: InvalidPropertyValidationResult[], result) => {
		if (result.isValid === false) {
			acc.push(result);
		}
		return acc;
	}, []);

export const isFieldPropertyName = (
	fieldFromIframe: FieldPublicShape,
	propertyName: string,
): propertyName is keyof FieldPublicShape =>
	Object.prototype.hasOwnProperty.call(fieldFromIframe, propertyName);
